import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { InsuranceProgramFragment, useSaveOrderServicesMutation, SaveOrderServicesMutation } from '@websky/graphql';
import { CartServiceInsurance, OnDeleteItemType } from '../../../Cart/types';
import { AdditionalServiceType } from '../../types';
import { getInsurances, getIsConfirmed, getOrder } from '../../../Checkout/store/order/selectors';
import { getPassengerState } from '../../../Checkout/store/passengers/selectors';
import { fillOrder } from '../../../Checkout/store/order/actions';
import { ThemeProvider, useTheme } from '../../../theme';
import { useConfig } from '../../../context';
import { useInsurance } from './hooks';
import { useIsMobile } from '../../../utils';
import { useRenderers } from '../../../renderProps';
import CheckoutAdditionalService from '../../CheckoutAdditionalService/CheckoutAdditionalService';
import DialogContent from '../../../AdditionalServices@deprecated/components/Insurance/DialogContent/DialogContent';
import InsuranceSummary from '../../../ServiceSummary/services/InsuranceSummary';
import ServiceSummaryOption from '../../../ServiceSummary/components/ServiceSummaryOption';
import ServiceSummaryDivider from '../../../ServiceSummary/components/ServiceSummaryDivider/ServiceSummaryDivider';
import DownloadInsurances from '../../../AdditionalServices@deprecated/components/Insurance/DownloadInsurances/DownloadInsurances';
import WarningModal from '../../../WarningModal';
import InsurancePopup from '../../../InsurancePopup';
import Modal from '../../../Modal';
import Close from '../Close/Close';
import icons from '../../icons';
import { useInsuranceGroupTexts } from '../InsuranceUpsale/hooks';
import { analytics_removeInsurance } from '../../../Insurance/utils';
import {
	addLoadingServiceParam,
	clearLoadingServicesParams
} from '../../../Checkout/store/loadingServicesParams/actions';
import { LoadingServiceType } from '../../../Checkout/store/loadingServicesParams/reducers';
import { getIsLoadingServiceByParams } from '../../../Checkout/store/loadingServicesParams/selectors';
import { useEffect } from 'react';
import { SafeHtml } from '@shared/ui';

interface Props {
	aggregationMode?: boolean;
	insurancePrograms: InsuranceProgramFragment[];
	groupName?: string;
	asSingle?: boolean;
}

const InsuranceAdditionalService: React.FC<Props> = ({ insurancePrograms, groupName, asSingle, aggregationMode }) => {
	const { t } = useTranslation('Checkout');
	const { Insurance: cardStyles } = useTheme('CheckoutAdditionalService');
	const { Insurance: css } = useTheme('AdditionalServices');
	const texts = useInsuranceGroupTexts(groupName, insurancePrograms);

	const {
		global: { checkoutAdditionalService }
	} = useConfig();

	const dispatch = useDispatch();

	const order = useSelector(getOrder);
	const isConfirmed = useSelector(getIsConfirmed);
	const insurances = useSelector(getInsurances);
	const passengers = useSelector(getPassengerState);

	const isLoadingByParams = useSelector(
		getIsLoadingServiceByParams({ type: LoadingServiceType.Insurance }, ['type'])
	);

	const isMobile = useIsMobile();

	const [saveOrderServicesMutation, { loading }] = useSaveOrderServicesMutation();

	useEffect(() => {
		if (loading) {
			dispatch(addLoadingServiceParam({ type: [LoadingServiceType.Insurance] }));
		} else {
			dispatch(clearLoadingServicesParams());
		}
	}, [loading]);

	const onUpdateOrder = (data: SaveOrderServicesMutation) => {
		dispatch(fillOrder(data.SaveOrderServices));
	};

	const {
		// common
		multiInsurance,
		selectedInsurances,
		buttons,
		minPrice,
		totalPrice,
		error,
		onSave,
		onAddInsurance,
		onRemoveInsurance,
		clearError,

		// insurance modal
		insuranceModalIsOpen,
		closeInsuranceModal,

		// confirmation modal
		confirmationModalIsOpen,
		closeConfirmationModal,

		// download modal
		downloadModalIsOpen,
		closeDownloadModal
	} = useInsurance<SaveOrderServicesMutation>({
		orderId: order.id,
		insurances,
		insurancePrograms,
		passengers,
		onSaveServices: saveOrderServicesMutation,
		onUpdateOrder,
		loading: isLoadingByParams
	});

	const hasInsurancePriceToPay = order.priceToPay?.insurance !== null;

	const byCodeMap = checkoutAdditionalService?.insurance?.insuranceProgramCodeMap?.[insurancePrograms[0].code];

	const headerIcon = React.useMemo<React.ReactNode>(() => {
		if (asSingle && byCodeMap?.headerIcon) {
			return byCodeMap.headerIcon;
		}

		return icons.insuranceIcon;
	}, [asSingle, byCodeMap]);

	const isSingleNotSelected = asSingle && !selectedInsurances.length;

	const style = React.useMemo<React.CSSProperties>(() => {
		if (isSingleNotSelected && byCodeMap?.color) {
			return { background: byCodeMap.color };
		}

		return null;
	}, [isSingleNotSelected, byCodeMap]);

	const endAdornment = React.useMemo(() => {
		if (isSingleNotSelected && byCodeMap?.contentIcon) {
			return <div className={cardStyles.serviceWrapperEndAdornment}>{byCodeMap.contentIcon}</div>;
		}

		return null;
	}, [isSingleNotSelected, byCodeMap]);

	const header = React.useMemo(() => {
		if (asSingle) {
			const insuranceProgram = insurancePrograms[0];
			const isImportant = insuranceProgram.important;

			if (isImportant && insuranceProgram.name) {
				return insuranceProgram.name;
			} else if (byCodeMap?.header) {
				return byCodeMap.header;
			}
		}

		return texts.title;
	}, [asSingle, insurancePrograms, byCodeMap, texts]);

	const renderCloseButton = (onClick: () => void) => {
		return <Close onClick={onClick} />;
	};

	const onInsuranceItemRemove: OnDeleteItemType = async (_, payload) => {
		const insuranceService = payload as CartServiceInsurance;
		const filterCodes = insurances.selectedInsurances
			.filter(insurance => insurance.insuranceProgram.code !== insuranceService.code)
			.map(insurance => insurance.insuranceProgram.code);

		const deletingProgram = insurances.selectedInsurances.find(
			selected => selected.insuranceProgram.code === (payload as CartServiceInsurance).code
		)?.insuranceProgram;

		if (deletingProgram) {
			analytics_removeInsurance(deletingProgram, order.travellers);
		}

		await onSave(filterCodes);
	};

	const { CheckoutAdditionalService: CustomCheckoutAdditionalService } = useRenderers();
	const ServiceWrapper = CustomCheckoutAdditionalService?.CheckoutAdditionalService ?? CheckoutAdditionalService;

	return (
		<>
			<InsuranceSummary
				onServiceDelete={hasInsurancePriceToPay ? onInsuranceItemRemove : null}
				selectedInsurances={selectedInsurances}
				isConfirmed={isConfirmed}
			>
				{selectedInsurances => (
					<ServiceWrapper
						style={style}
						icon={headerIcon}
						className={cn(cardStyles.insurance, insurancePrograms[0].code, {
							[cardStyles.withEndAdornment]: !!endAdornment
						})}
						insuranceCode={insurancePrograms[0].code}
						isLoading={isLoadingByParams}
						addClick={onAddInsurance}
						header={header}
						isSelected={!!selectedInsurances.length}
						priceFrom={minPrice}
						priceTotal={totalPrice}
						description={texts.descr}
						buttons={buttons}
						endAdornment={endAdornment}
						mode={aggregationMode ? 'aggregator' : 'default'}
						coverClassName={cardStyles.insurance_aggregator}
						serviceType={AdditionalServiceType.Insurance}
						servicesCount={insurancePrograms.length}
					>
						<ThemeProvider
							value={{
								ServiceSummary: {
									ServiceSummaryOption: {
										option: cardStyles.selectedOption,
										option__name: cardStyles.option__name
									}
								}
							}}
						>
							<ServiceSummaryDivider>
								<ServiceSummaryOption
									groupOption={{
										items: selectedInsurances
									}}
								/>
							</ServiceSummaryDivider>
						</ThemeProvider>

						{selectedInsurances.length === 1 && (
							<SafeHtml tag="div" html={selectedInsurances[0].description} />
						)}
					</ServiceWrapper>
				)}
			</InsuranceSummary>
			{multiInsurance && (
				<Modal
					classes={{
						scrollBody: css.scrollBody,
						paper: css.paper
					}}
					open={insuranceModalIsOpen}
					slideUp={isMobile}
					onClose={closeInsuranceModal}
					closeButtonRenderer={renderCloseButton}
				>
					<InsurancePopup
						header={header}
						description={insurancePrograms[0]?.description}
						insurances={insurancePrograms}
						selectedInsurances={selectedInsurances}
						onClose={closeInsuranceModal}
						onSave={onSave}
						isLoading={isLoadingByParams}
						travellers={order.travellers}
					/>
				</Modal>
			)}

			<Modal
				maxWidth="sm"
				classes={{ scrollBody: css.scrollBody, paper: cn(css.paper, css.paper_confirm) }}
				open={confirmationModalIsOpen}
				onClose={closeConfirmationModal}
				slideUp={isMobile}
			>
				<DialogContent
					closeConfirmationPopup={closeConfirmationModal}
					onInsuranceRemove={onRemoveInsurance}
					refusingIsInProgress={loading}
				/>
			</Modal>

			<Modal
				maxWidth="sm"
				classes={{ scrollBody: css.scrollBody, paper: cn(css.paper, css.paper_download) }}
				open={downloadModalIsOpen}
				onClose={closeDownloadModal}
				slideUp={isMobile}
			>
				<DownloadInsurances selectedInsurances={selectedInsurances} />
			</Modal>

			<WarningModal
				title={t('Oops, something went wrong')}
				content={t(
					'An unexpected error has occurred during the booking process. Please try again later or contact a customer support service.'
				)}
				errorMessage={t(error?.message ?? '')}
				isOpen={!!error}
				onClose={clearError}
				onButtonClick={clearError}
			/>
		</>
	);
};

export default InsuranceAdditionalService;
